<template>
  <div v-if="pendingAction && preview"
    class="rounded-lg border px-3 py-2 text-xs"
    :class="isPending ? 'border-primary/30 bg-primary/5' : 'border-gray-200 bg-gray-50'"
    data-testid="ai-inline-decision" :data-status="status ?? undefined">
    <div class="flex items-start gap-2">
      <i class="pi mt-0.5" :class="isPending ? 'pi-question-circle text-primary' : 'pi-info-circle text-gray-400'"
        aria-hidden="true"></i>
      <div class="min-w-0 flex-1">
        <div class="font-medium text-gray-800 truncate" data-testid="ai-inline-decision-title">{{ preview.title }}</div>
        <div v-if="isPending && preview.summary.length" class="text-gray-500 truncate">{{ preview.summary[0] }}</div>
        <div v-if="isPending && changes.length" class="text-gray-500" data-testid="ai-inline-decision-changes">
          {{ changes.length === 1 ? '1 change' : `${changes.length} changes` }} — see details
        </div>
        <div v-if="!isPending" class="text-gray-500" data-testid="ai-inline-decision-outcome">{{ outcome }}</div>
        <div v-if="isPending && preview.warnings.length" class="text-amber-700 truncate"
          data-testid="ai-inline-decision-warning">
          <i class="pi pi-exclamation-triangle text-[10px]" aria-hidden="true"></i>
          {{ preview.warnings.length === 1 ? preview.warnings[0] : `${preview.warnings.length} warnings — see details` }}
        </div>
      </div>
      <button type="button" class="shrink-0 text-primary hover:underline" data-testid="ai-inline-decision-details"
        @click="emit('details')">
        Details
      </button>
    </div>

    <p v-if="actionError" class="mt-1 text-red-600" data-testid="ai-inline-decision-error">{{ actionError }}</p>

    <div v-if="resultTarget" class="flex justify-end mt-1">
      <Button :label="resultTarget.label" icon="pi pi-external-link" size="small" text
        data-testid="ai-inline-open-result" @click="openResult" />
    </div>

    <div v-if="isPending" class="flex justify-end gap-2 mt-2">
      <Button label="Decline" severity="secondary" text size="small"
        :disabled="!canCancel" :loading="submitting === 'cancel'"
        :aria-label="`Decline: ${preview.title}`" data-testid="ai-inline-decline" @click="cancel" />
      <Button :label="preview.confirmLabel" :icon="preview.destructive ? 'pi pi-exclamation-triangle' : 'pi pi-check'"
        :severity="preview.destructive ? 'danger' : undefined" size="small"
        :disabled="!canConfirm" :loading="submitting === 'confirm'"
        :aria-label="`Accept: ${preview.confirmLabel}`" data-testid="ai-inline-accept" @click="confirm" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAiPendingAction } from '@/composables/ai/useAiPendingAction';
import { AiPendingActionStatus } from '@/types/composition-functions';
import { pendingStatusLabel } from '@/utils/aiActions';

/**
 * A proposed change as one compact row in the floating chat: the server's
 * title, its first summary line, and Accept / Decline. It drives the same
 * pending action as the full `action_confirmation` card (same composable,
 * same mutations, same one-confirm guard), so accepting here or there is the
 * same act; "Details" opens the full card in the workspace.
 */
const props = defineProps<{ pendingActionId: string }>();
const emit = defineEmits<{ details: [] }>();

const {
  pendingAction, preview, run, status, canConfirm, canCancel, submitting, actionError, confirm, cancel,
  changes, resultTarget, openResult,
} = useAiPendingAction(() => props.pendingActionId);

const isPending = computed(() => status.value === AiPendingActionStatus.Pending);

const outcome = computed(() => {
  if (!status.value) return '';
  if (status.value === AiPendingActionStatus.Executing || (run.value && status.value !== AiPendingActionStatus.Done)) {
    return 'Running — open Details to follow it.';
  }
  return pendingAction.value?.resultMessage || pendingStatusLabel(status.value);
});
</script>
